import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLocation, useMatch, useNavigate } from 'react-router-dom';

import {
    getCatalogItems,
    getCatalogItemsAreLoading,
    getCatalogItemsShowDetailedView,
    getSelectedCatalogItem,
} from 'entities/CatalogItem/model/selectors/catalogItemSelectors';
import { catalogItemActions } from 'entities/CatalogItem/model/slices/CatalogItemSlice';
import { CatalogItem } from 'entities/CatalogItem/model/types/CatalogItemSchema';
import { getSelectedOrderId } from 'entities/Order/model/selectors/ordersSelectors';
import {
    getSelectedPropertyCatalogId,
    getSelectedPropertyId,
} from 'entities/Property/model/selectors/PropertySelectors';
import { getUserCanSeePrices } from 'entities/User/model/selectors/userPermissionsSeletors';
import { ReviewOrderButton, ReviewOrderFooter } from 'features/AddItemsToOrder';
import { addItemsToOrderIsLoading } from 'features/AddItemsToOrder/model/selectors/addItemsToOrderSelectors';
import { getSearchCatalogItemIsActive } from 'features/SearchCatalogItems/model/selectors/searchCatalogItemsSelectors';
import { getRouteOrderCatalogInventoryItem } from 'shared/const/router';
import { useAppDispatch } from 'shared/lib/hooks/useAppDispatch';
import { useFeatureFlag } from 'shared/lib/hooks/useFeatureFlag';
import { useInitialEffect } from 'shared/lib/hooks/useInitialEffect';
import {
    getInventoryTableItems,
    getInventoryTableItemsLoading,
} from 'widgets/InventoryTable/model/selectors/catalogItemSelectors';
import { fetchInventoryItems } from 'widgets/InventoryTable/model/services/fetchCatalogItems/fetchInventoryItems';
import {
    PropertyCatalogItemDataDefiner,
    PropertyCatalogItemsMoveTo,
} from 'widgets/PropertyCatalog';
import { SearchCatalogItems } from 'widgets/SearchCatalogItems';

import { OrderInventoryTableRow } from './OrderInventoryTableRow';
import { OrderInventoryTableRowSkeleton } from './OrderInventoryTableRowSkeleton';

import { columnWidths } from '../../lib/inventoryTableRowSpec';
import { InventoryTableEmpty } from '../InventoryTable/InventoryTableEmpty';

export const OrderInventoryTable = () => {
    const dispatch = useAppDispatch();
    const location = useLocation();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const data = useSelector(getInventoryTableItems);

    const catalogPricesVisible = useFeatureFlag('catalog_prices_is_visible');

    const currentPageIsOrders = useMatch('/orders' + '/*');
    const selectedOrderId = useSelector(getSelectedOrderId);
    const selectedPropertyId = useSelector(getSelectedPropertyId);
    const selectedItem = useSelector(getSelectedCatalogItem);
    const detailedViewIsShown = useSelector(getCatalogItemsShowDetailedView);
    const isActiveSearch = useSelector(getSearchCatalogItemIsActive);
    const catalogItemsListLoading = useSelector(getCatalogItemsAreLoading);
    const inventoryListLoading = useSelector(getInventoryTableItemsLoading);
    const isLoading = isActiveSearch
        ? catalogItemsListLoading
        : inventoryListLoading;
    const catalogItems = useSelector(getCatalogItems) as CatalogItem[];
    const selectedPropertyCatalogId = useSelector(getSelectedPropertyCatalogId);
    const canSeePrices =
        useSelector(getUserCanSeePrices) && catalogPricesVisible;
    const isLoadingUpdateOrder = useSelector(addItemsToOrderIsLoading);

    const inventoryOnlyItems = useMemo(() => {
        return catalogItems
            ? catalogItems.filter((item) => item.isInventory)
            : [];
    }, [catalogItems]);

    const dataToDisplay = isActiveSearch ? inventoryOnlyItems : data;

    const showNoInventorySearchResultsHelper =
        isActiveSearch && !isLoading && dataToDisplay.length === 0;

    const orderType = location.state?.orderType;
    const catalogId =
        location.state?.selectedPropertyCatalogId || selectedPropertyCatalogId;

    useInitialEffect(async () => {
        await dispatch(fetchInventoryItems(catalogId));
    });

    const onExpandRow = useCallback(
        (item: CatalogItem) => () => {
            dispatch(catalogItemActions.setSelectedItem(item));
            dispatch(catalogItemActions.setShowDetailedView(true));
            navigate(getRouteOrderCatalogInventoryItem(item.id), {
                state: {
                    selectedOrderId,
                    selectedPropertyCatalogId,
                    selectedPropertyId,
                    fromOrder: selectedOrderId,
                },
            });
        },
        [
            dispatch,
            navigate,
            selectedOrderId,
            selectedPropertyCatalogId,
            selectedPropertyId,
        ],
    );

    return (
        <Box>
            {detailedViewIsShown && selectedItem ? (
                <Box
                    mt="10px"
                    p="16px"
                    borderRadius="8px"
                    border="1px solid"
                    borderColor="divider"
                >
                    <PropertyCatalogItemDataDefiner
                        isDetailed
                        item={selectedItem}
                        isOrderPage={!!currentPageIsOrders}
                    />
                </Box>
            ) : (
                <>
                    <SearchCatalogItems
                        catalogId={catalogId}
                        moveTo={PropertyCatalogItemsMoveTo.ORDER}
                        isInventory
                        hideField
                    />
                    <Box
                        sx={{
                            overflowY: 'scroll',
                            overflowX: 'hidden',
                            padding: '8px 16px',
                            backgroundColor: 'rgba(233, 232, 232, 0.30)',
                            height: 'calc(100vh - 466px)',
                            borderRadius: '8px',
                        }}
                    >
                        <Box
                            height={'calc(100vh - 482px)'}
                            sx={{ overflowY: 'scroll' }}
                            data-testid="Order-inventory-table"
                        >
                            {showNoInventorySearchResultsHelper ? (
                                <InventoryTableEmpty
                                    isActiveSearch
                                    isOrdersAddItemsFlow={true}
                                />
                            ) : (
                                <Table
                                    sx={{
                                        '& .MuiTableCell-root': {
                                            borderBottom: 'none',
                                        },
                                        borderCollapse: 'separate',
                                        borderSpacing: '0 8px',
                                    }}
                                >
                                    <TableHead
                                        sx={{
                                            '& th': {
                                                paddingTop: '0px',
                                                paddingBottom: '0px',
                                            },
                                        }}
                                    >
                                        <TableRow>
                                            <TableCell
                                                data-testid="inventory-title-label"
                                                colSpan={2}
                                            >
                                                <Typography typography="openSans.captionMedium">
                                                    {t('Item')}
                                                </Typography>
                                            </TableCell>
                                            <TableCell
                                                align="center"
                                                width={columnWidths.sku}
                                                data-testid="inventory-sku-label"
                                            >
                                                <Typography typography="openSans.captionMedium">
                                                    {t('SKU')}
                                                </Typography>
                                            </TableCell>
                                            <TableCell
                                                align="center"
                                                width={columnWidths.qtyPerUom}
                                                data-testid="inventory-qty-uom-label"
                                            >
                                                <Typography typography="openSans.captionMedium">
                                                    {t('Qty/UOM')}
                                                </Typography>
                                            </TableCell>
                                            <TableCell
                                                align="center"
                                                data-testid="inventory-uom-label"
                                            >
                                                <Typography typography="openSans.captionMedium">
                                                    {t('UOM')}
                                                </Typography>
                                            </TableCell>
                                            <TableCell
                                                align="center"
                                                data-testid="inventory-min-on-hand-label"
                                                width={columnWidths.minOnHand}
                                            >
                                                <Typography typography="openSans.captionMedium">
                                                    {t('Min/Hand')}
                                                </Typography>
                                            </TableCell>
                                            {canSeePrices && (
                                                <TableCell
                                                    align="right"
                                                    data-testid="inventory-price-label"
                                                >
                                                    <Typography typography="openSans.captionMedium">
                                                        {t('Price/UOM')}
                                                    </Typography>
                                                </TableCell>
                                            )}
                                            <TableCell
                                                padding="checkbox"
                                                width={columnWidths.buttons}
                                            ></TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {isLoading
                                            ? Array.from({ length: 3 }).map(
                                                  (_, index) => (
                                                      <OrderInventoryTableRowSkeleton
                                                          key={index}
                                                          canSeePrices={
                                                              canSeePrices
                                                          }
                                                      />
                                                  ),
                                              )
                                            : dataToDisplay?.map((row) => (
                                                  <OrderInventoryTableRow
                                                      key={row.id}
                                                      item={row}
                                                      canSeePrices={
                                                          canSeePrices
                                                      }
                                                      onExpandRow={onExpandRow(
                                                          row,
                                                      )}
                                                  />
                                              ))}
                                    </TableBody>
                                </Table>
                            )}
                        </Box>
                    </Box>
                    <ReviewOrderFooter>
                        <ReviewOrderButton
                            isLoadingUpdateOrder={isLoadingUpdateOrder}
                            orderType={orderType}
                        />
                    </ReviewOrderFooter>
                </>
            )}
        </Box>
    );
};
